import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { customerInvoicesApi, customerPaymentsApi } from '../lib/api';
import { X, CreditCard, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

interface PaymentModalProps {
  invoiceId: string;
  amount: number;
  currency: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

function PaymentForm({
  invoiceId,
  amount,
  currency,
  onSuccess,
}: {
  invoiceId: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Payment failed');
      setProcessing(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/invoices/${invoiceId}?payment=success`,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment failed');
      setProcessing(false);
    } else {
      setSuccess(true);
      setTimeout(onSuccess, 1500);
    }
  }

  function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
        <h3 className="mt-4 text-xl font-semibold text-gray-900">{t('payment.successTitle')}</h3>
        <p className="mt-2 text-gray-600">{t('payment.successBody')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">{t('payment.amountToPay')}</span>
          <span className="text-2xl font-bold">{formatCurrency(amount, currency)}</span>
        </div>
      </div>

      <PaymentElement />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={clsx(
          'w-full py-3 px-4 rounded-lg font-medium text-white transition-colors',
          processing || !stripe
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-brand-600 hover:bg-brand-700'
        )}
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('payment.processing')}
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <CreditCard className="h-5 w-5" />
            Pay {formatCurrency(amount, currency)}
          </span>
        )}
      </button>
    </form>
  );
}

export default function PaymentModal({ invoiceId, amount, currency, onClose, onSuccess }: PaymentModalProps) {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [payingWithSaved, setPayingWithSaved] = useState(false);
  /** Card payments are switched off entirely, as opposed to this one failing. */
  const [cardUnavailable, setCardUnavailable] = useState(false);

  useEffect(() => {
    async function init() {
      try {
        // Load saved payment methods
        const methodsResponse = await customerPaymentsApi.getMethods();
        const methods = (methodsResponse.data as any)?.methods || [];
        setSavedCards(methods);
        
        // If no saved cards, show new card form
        if (methods.length === 0) {
          setUseNewCard(true);
        } else {
          // Select default card
          const defaultCard = methods.find((c: SavedCard) => c.isDefault);
          setSelectedCard(defaultCard?.id || methods[0]?.id);
        }

        // Create payment intent
        const intentResponse = await customerInvoicesApi.createPaymentIntent(invoiceId);
        setClientSecret(intentResponse.data?.clientSecret || null);
      } catch (err) {
        // 503 from the API means nobody has configured a card gateway. That is
        // not a failure the customer can retry their way out of, so it gets a
        // way forward rather than a red box.
        const status = (err as any)?.status;
        if (status === 503) setCardUnavailable(true);
        else setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [invoiceId]);

  async function handlePayWithSavedCard() {
    if (!selectedCard || !clientSecret) return;

    setPayingWithSaved(true);
    setError(null);

    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not loaded');

      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedCard,
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
      } else {
        onSuccess();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPayingWithSaved(false);
    }
  }

  function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">{t('payment.payInvoice')}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : cardUnavailable ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
                We are not taking card payments at the moment. You can pay this invoice by bank
                transfer and upload your receipt — we activate as soon as we confirm it.
              </div>
              <button
                onClick={() => {
                  onClose();
                  document.getElementById('bank-transfer')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium"
              >
                {t('payment.payByTransfer')}
              </button>
            </div>
          ) : error && !clientSecret ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </div>
          ) : useNewCard && clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <PaymentForm
                invoiceId={invoiceId}
                amount={amount}
                currency={currency}
                onSuccess={onSuccess}
              />
              {savedCards.length > 0 && (
                <button
                  onClick={() => setUseNewCard(false)}
                  className="mt-4 w-full text-center text-brand-600 hover:text-brand-800 text-sm"
                >
                  {t('payment.useSavedCard')}
                </button>
              )}
            </Elements>
          ) : (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('payment.amountToPay')}</span>
                  <span className="text-2xl font-bold">{formatCurrency(amount, currency)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">{t('payment.selectMethod')}</label>
                {savedCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setSelectedCard(card.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-colors',
                      selectedCard === card.id
                        ? 'border-brand-600 bg-brand-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <CreditCard className="h-6 w-6 text-gray-400" />
                    <div className="flex-1 text-left">
                      <p className="font-medium capitalize">{card.brand} •••• {card.last4}</p>
                      <p className="text-sm text-gray-500">
                        Expires {card.expMonth}/{card.expYear}
                        {card.isDefault && ' • Default'}
                      </p>
                    </div>
                    {selectedCard === card.id && (
                      <CheckCircle className="h-5 w-5 text-brand-600" />
                    )}
                  </button>
                ))}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              <button
                onClick={handlePayWithSavedCard}
                disabled={!selectedCard || payingWithSaved}
                className={clsx(
                  'w-full py-3 px-4 rounded-lg font-medium text-white transition-colors',
                  !selectedCard || payingWithSaved
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700'
                )}
              >
                {payingWithSaved ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('payment.processing')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Pay {formatCurrency(amount, currency)}
                  </span>
                )}
              </button>

              <button
                onClick={() => setUseNewCard(true)}
                className="w-full text-center text-brand-600 hover:text-brand-800 text-sm"
              >
                {t('payment.useNewCard')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
